
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { StoreModel } from '@/models/store';
import ActionButton from '@/components/ActionButton.vue';
import Alert from '@/components/Alert.vue';
import GoogleDriveSelector from '@/components/GoogleDriveSelector.vue';
import PluginConfiguration from '@/components/PluginConfiguration.vue';
import IncomingInventorySpreadSheetSettings from '@/components/IncomingInventorySpreadSheetSettings.vue';
import QuickbooksIntegration from '@/components/QuickbooksIntegration.vue';
import { ReplenishmentSettingRecordModel, NoEtaHandling } from '@/models/replenishment_setting_record';
import {post, get, put} from "@/services/http";
import { EventBus } from "@/main";

@Component({
  components: {
    ActionButton,
    Alert,
    GoogleDriveSelector,
    PluginConfiguration,
    IncomingInventorySpreadSheetSettings,
    QuickbooksIntegration
  },
})
export default class IncomingInventorySettings extends Vue {
  @Prop({default: null})
  public app!: any;
  @Prop({ default: () => new StoreModel() })
  public store!: StoreModel;
  public success: boolean = false;
  public error: boolean = false;
  public loading: boolean = false;
  public selectSourceLoading: boolean = false;
  public activeTab: string = '';
  public assistySettings: any = {
    includeUnconfirmedPOs: false,
  };

  // No-ETA handling setting
  public noEtaHandling: NoEtaHandling = 'after';
  public loadingNoEtaHandling: boolean = false;
  public noEtaHandlingOptions: Array<{ label: string; value: NoEtaHandling }> = [
    {
      label: 'Treat as arriving after lead time (Conservative - default)',
      value: 'after'
    },
    {
      label: 'Treat as arriving before lead time (Optimistic)',
      value: 'before'
    }
  ];

  get noEtaHandlingHint(): string {
    if (this.noEtaHandling === 'before') {
      return 'Incoming inventory without ETA will extend days of inventory remaining';
    }
    return 'Incoming inventory without ETA will reduce reorder quantity (recommended)';
  }

  public sources: object[] = [
    {
      value: 'assisty',
      text: 'Purchase Orders from Assisty (Default)',
      disabled: true
    },
    {
      value: 'shopify',
      text: 'Purchase Orders from Shopify'
    },
    {
      value: 'spreadsheets',
      text: 'Google speardsheets file'
    },
    {
      value: 'stocky',
      text: 'Purchase Orders from app Stocky'
    },
    {
      value: 'quickbooks',
      text: 'Purchase Orders from QuickBooks'
    }
  ];
  public selectedSources: string[] = [];

  public getSourceText(value: string): string {
    const source = this.sources.find((s: any) => s.value === value);
    return source ? (source as any).text : value;
  }

  public async created() {
    try {
      this.loading = true;

      // Fetch no-ETA handling setting
      await this.fetchNoEtaHandling();

      const response: any = await get('/incoming_inventory/source');
      if (response.data.source) {
        // If source is a string, split it into array
        if (typeof response.data.source === 'string') {
          const sources = response.data.source.split(',').filter(Boolean);
          this.selectedSources = ['assisty', ...sources.filter((s: string) => s !== 'assisty')];
        } else {
          const sourceArray = Array.isArray(response.data.source)
            ? response.data.source
            : [response.data.source];
          this.selectedSources = ['assisty', ...sourceArray.filter((s: string) => s !== 'assisty')];
        }
        // Set initial active tab
        if (this.selectedSources.length > 0) {
          this.activeTab = this.selectedSources[0];
        }
      } else {
        // If no other sources are set, at least set 'assisty'
        this.selectedSources = ['assisty'];
      }

      console.log(this.selectedSources);
      if (this.selectedSources.includes('not_set_up_yet')) {
        // remove not_set_up_yet from selectedSources
        this.selectedSources = this.selectedSources.filter((s: string) => s !== 'not_set_up_yet');
      }

      const assistySettingsResponse: any = await get('/incoming_inventory/include_unconfirmed_assisty_po');
      this.assistySettings.includeUnconfirmedPOs = assistySettingsResponse.includeUnconfirmedPOs;

      this.success = true;
    } catch (e) {
      console.error('Failed to load incoming inventory settings', e);
    } finally {
      this.loading = false;
    }
  }

  public async fetchNoEtaHandling() {
    this.loadingNoEtaHandling = true;
    try {
      this.noEtaHandling = await ReplenishmentSettingRecordModel.getNoEtaHandling();
    } catch (error) {
      console.error('Failed to fetch no-ETA handling setting:', error);
    } finally {
      this.loadingNoEtaHandling = false;
    }
  }

  public async updateNoEtaHandling(handling: NoEtaHandling) {
    this.loadingNoEtaHandling = true;
    try {
      await ReplenishmentSettingRecordModel.updateNoEtaHandling(handling);
      EventBus.$emit('show-snackbar', 'No-ETA handling setting updated', 'success');
    } catch (error) {
      console.error('Failed to update no-ETA handling setting:', error);
      EventBus.$emit('show-snackbar', 'Failed to update setting', 'error');
      // Revert to previous value
      await this.fetchNoEtaHandling();
    } finally {
      this.loadingNoEtaHandling = false;
    }
  }

  public async updateSelectedSources(sources: string[]) {
    // Ensure 'assisty' is always included
    if (!sources.includes('assisty')) {
      sources = ['assisty', ...sources];
    }
    // If no other sources selected, just keep 'assisty'
    const sourceString = sources.filter(Boolean).join(',');
    await post('/incoming_inventory/source', {source: sourceString});
    // Update active tab if current tab is removed
    if (!sources.includes(this.activeTab)) {
      this.activeTab = sources[0];
    }
  }

  public async updateAssistyPOSettings() {
    try {
      await put('/incoming_inventory/include_unconfirmed_assisty_po', this.assistySettings);
    } catch (e) {
      console.error('Failed to update Assisty PO settings', e);
    }
  }
}
